import { Alert, Button, Card, Col, Divider, Radio, Result, Row, Skeleton, Space, Typography, notification } from "antd";
import { LockKeyhole } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useRecoilValue } from "recoil";

import { isMfaRequired } from "../../backend/interceptors";
import { projectSecurityApi, ProjectSecurityPolicy } from "../../backend/project-security";
import { useDoubleConfigurationModal } from "../../contexts/Modal";
import { useAuth } from "../../contexts/Auth";
import { currentProjectState } from "../../recoil/project";
import { getUsersWithinProject } from "../../utils/project/get-users";

export default function ProjectSettingsSecurity() {
    const { projectId } = useParams() as { projectId: string };
    const { profile } = useAuth();
    const navigate = useNavigate();
    const { open: openConfirmModal } = useDoubleConfigurationModal();
    const currentProject = useRecoilValue(currentProjectState);
    const [policy, setPolicy] = useState<ProjectSecurityPolicy>();
    const [etag, setEtag] = useState<string>();
    const [mode, setMode] = useState<ProjectSecurityPolicy["mode"]>("optional");
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [canManage, setCanManage] = useState(false);
    const [mfaRequired, setMfaRequired] = useState(false);
    const [federatedMembers, setFederatedMembers] = useState<string[]>([]);

    const load = useCallback(async () => {
        try {
            setLoading(true);
            setMfaRequired(false);
            setFederatedMembers([]);
            const [{ users, userProfiles }, response] = await Promise.all([getUsersWithinProject(projectId), projectSecurityApi.get(projectId)]);
            const currentPolicy = response.policy;
            const currentUser = users.find(user => user.id === profile?.id);
            setCanManage(currentUser?.permission === "owner" || currentUser?.permission === "admin");
            // Google and GitHub accounts rely on their provider's two-step verification.
            // `federated` ships ahead of the generated SDK type.
            setFederatedMembers(users
                .map(user => userProfiles[user.id] as (typeof userProfiles[string] & { federated?: boolean }) | undefined)
                .filter(member => member?.federated)
                .map(member => member!.email)
                .filter(Boolean));
            setPolicy(currentPolicy);
            setMode(currentPolicy.mode);
            setEtag(response.etag);
        } catch (error: any) {
            if (isMfaRequired(error)) {
                setMfaRequired(true);
            } else {
                notification.error({ message: "Couldn't load project security", description: error?.response?.data?.message || "Please try again." });
            }
        } finally {
            setLoading(false);
        }
    }, [profile?.id, projectId]);

    useEffect(() => {
        load();
    }, [load]);

    const save = async () => {
        if (!policy || !etag || mode === policy.mode) {
            return;
        }
        try {
            setSaving(true);
            const updated = await projectSecurityApi.update(projectId, mode, etag);
            setPolicy(updated.policy);
            setMode(updated.policy.mode);
            setEtag(updated.etag);
            notification.success({ message: "Project security policy updated" });
        } catch (error: any) {
            // MFA_REQUIRED is handled globally by the response interceptor.
            if (error?.response?.status === 412) {
                notification.warning({ message: "This policy changed elsewhere", description: "The latest policy has been loaded. Review it before saving." });
                await load();
            } else if (error?.response?.status === 428) {
                notification.warning({ message: "Reloading the latest policy", description: "The server requires the current version before it can save this setting." });
                await load();
            } else {
                notification.error({ message: "Couldn't update project security", description: error?.response?.data?.message || "Please try again." });
            }
        } finally {
            setSaving(false);
        }
    };

    const handleSave = () => {
        if (mode !== "mfa_required") {
            save();
            return;
        }
        openConfirmModal({
            title: "Require two-step verification for all members?",
            description: <>
                Members who sign in with email and haven't set up two-step verification will lose access to this project until they do.
            </>,
            confirmation: currentProject?.name || projectId,
            alert: <b>Members without two-step verification will lose access.</b>,
            okText: "Require MFA",
            cancelText: "Cancel",
            onOk: () => save()
        });
    };

    if (loading) {
        return <Card><Skeleton active /></Card>;
    }
    if (mfaRequired) {
        return <Card>
            <Result
                status="warning"
                title="This page needs a verified sign-in"
                subTitle="Sign in again with your authenticator app or passkey before viewing this project's security policy."
                extra={<Button type="primary" onClick={() => navigate("/account/security")}>Open account security</Button>}
            />
        </Card>;
    }
    if (!canManage) {
        return <Card>
            <Result
                status="warning"
                title="You are not authorized to manage project security."
                subTitle="Only project owners and admins can change this setting."
            />
        </Card>;
    }

    return <Card>
        <Card.Meta
            title={<Space><LockKeyhole size={17} /> Project security</Space>}
            description="Choose whether members need two-step verification to access this project."
        />
        <Divider />
        <Space direction="vertical" size="middle" style={{ width: "100%" }}>
            {federatedMembers.length > 0 && (
                <Alert
                    type="info"
                    showIcon
                    message={`${federatedMembers.length} member${federatedMembers.length === 1 ? "" : "s"} sign${federatedMembers.length === 1 ? "s" : ""} in with Google or GitHub`}
                    description={<>
                        <div>
                            They use that provider's two-step verification, so requiring MFA doesn't lock them out. Make sure it's on there.
                        </div>
                        <div style={{ marginTop: 8 }}>
                            {federatedMembers.slice(0, 5).join(", ")}
                            {federatedMembers.length > 5 ? ` and ${federatedMembers.length - 5} more` : ""}
                        </div>
                    </>}
                />
            )}
            <Radio.Group value={mode} onChange={event => setMode(event.target.value)}>
                <Space direction="vertical">
                    <Radio value="optional">
                        <Typography.Text strong>Optional MFA</Typography.Text>
                        <br />
                        <Typography.Text type="secondary">Members can access the project with their normal sign-in.</Typography.Text>
                    </Radio>
                    <Radio value="mfa_required">
                        <Typography.Text strong>Require MFA</Typography.Text>
                        <br />
                        <Typography.Text type="secondary">
                            Members need two-step verification to access this project. Google and GitHub members use their provider's.
                        </Typography.Text>
                    </Radio>
                </Space>
            </Radio.Group>
            {mode === "mfa_required" && (
                <Alert
                    type="warning"
                    showIcon
                    message="Members without two-step verification will lose access"
                    description="Members who sign in with email and haven't set up two-step verification will lose access to this project until they do. They'll be told how when they next open it. API keys keep working; creating a new one needs a verified sign-in."
                />
            )}
            {policy?.updatedAt && <Typography.Text type="secondary">Last updated {new Date(policy.updatedAt).toLocaleString()}</Typography.Text>}
        </Space>
        <Divider />
        <Row style={{ display: "flex", alignItems: "center" }}>
            <Col span={24} style={{ textAlign: "right" }}>
                <Button
                    type="primary"
                    loading={saving}
                    disabled={mode === policy?.mode}
                    onClick={handleSave}
                >
                    Save security policy
                </Button>
            </Col>
        </Row>
    </Card>;
}
